import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SolEstimatePricingMode } from '../../../models/enum';
import { SolEstimateTableCommonFunctionsService } from '../../../services/sol-estimate-table-common-functions/sol-estimate-table-common-functions.service';
import { SolEstimateTableMiscRiskTravelCosts } from '../../../models/sol-estimate-table/SolEstimateTableMiscRiskTravelCosts';
import { SolEstimateTableRowBaseExtended } from '../../../models/sol-estimate-table/SolEstimateSubContractorCostsRow';
import { MultilingualCurrencyPipe } from "../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { NumericMaskDirective } from '../../../shared/numeric/numeric-mask.directive';
import { MatInputModule } from '@angular/material/input';
import { SolEstimateRiskDialogData } from '../../../models/sol-estimate-table/SolEstimateRiskDialogData';
import { DecimalPrecisionPipe } from "../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { CommonDataService } from '../../../services/common-data.service';
import { EstimateTab } from '../../../models/dtos';
import { MISC_RISK_TRAVEL_SELL_PRICE_FLAGS, RISK_SELL_PRICE_FLAGS } from '../../../services/estimate/estimate-last-modified-sell-price.service';
import { Subject, takeUntil } from 'rxjs';
import { CONSTANTS } from '../../../Constants/Constants';

@Component({
  selector: 'app-sol-estimate-table-misc-risk-travel-costs',
  imports: [
    MatButtonModule,
    MatIconModule,
    CommonModule,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    NumericMaskDirective,
    MultilingualCurrencyPipe,
    DecimalPrecisionPipe
  ],
  templateUrl: './sol-estimate-table-misc-risk-travel-costs.component.html',
  styleUrl: './sol-estimate-table-misc-risk-travel-costs.component.scss'
})
export class SolEstimateTableMiscRiskTravelCostsComponent implements OnInit, OnDestroy {
  readonly mechanicalFeeRowName = 'MECHANICAL_FEE';
  readonly subcontractorFeeRowName = 'SUBCONTRACTOR_FEE';
  readonly projectRiskRowName = 'PROJECT_ENTERED_RISK_COSTS';

  @Input() isMarkUpSelected: boolean = false;
  @Input() sellAsPricingMode: SolEstimatePricingMode = SolEstimatePricingMode.Line_Item;
  @Input() miscRiskTravelCostsFormGroup: FormGroup<SolEstimateTableMiscRiskTravelCosts>;
  @Input() riskDialogRequiredData: SolEstimateRiskDialogData;
  @Input() isCollaborativeNotEdit: boolean = false;

  destroy$: Subject<void> = new Subject<void>();

  isMRTCostsExpanded: boolean = false;
  isRiskCostsExpanded: boolean = false;
  solEstimatePricingMode = SolEstimatePricingMode;

  /** Live EstimateTab, read directly by the template so flag updates are picked up in place. */
  estimateTab: EstimateTab | null = null;

  /** Maps row names to their sell-price flag names for misc/travel rows. */
  readonly miscRiskTravelSellPriceFlags = { ...MISC_RISK_TRAVEL_SELL_PRICE_FLAGS, ...RISK_SELL_PRICE_FLAGS };
  /** Maps row names to their sell-price flag names for risk rows. */
  readonly riskSellPriceFlags = RISK_SELL_PRICE_FLAGS;

  get isEstimateSapSubmitted(): boolean {
    return this.solEstimateTableCommonFunctionsService.isEstimateSapSubmitted;
  }

  constructor(
    public readonly solEstimateTableCommonFunctionsService: SolEstimateTableCommonFunctionsService,
    private readonly commonDataService: CommonDataService
  ) { }

  ngOnInit(): void {
    if (!this.miscRiskTravelCostsFormGroup) {
      return;
    }
    this.commonDataService.projectObjectDataAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAvailable: boolean) => {
        if (isAvailable) {
          this.estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab ?? null;
        }
      });
  }

  get subMiscTravelFormArray(): FormArray<FormGroup<SolEstimateTableRowBaseExtended>> {
    return this.miscRiskTravelCostsFormGroup.controls.rows;
  }

  get subRiskHeaderRow(): FormGroup<SolEstimateTableRowBaseExtended> {
    return this.miscRiskTravelCostsFormGroup.controls.riskCosts.controls.headerRow;
  }

  get subRiskFormArray(): FormArray<FormGroup<SolEstimateTableRowBaseExtended>> {
    return this.miscRiskTravelCostsFormGroup.controls.riskCosts.controls.rows;
  }

  /** Risk is part of PPM: the project risk cost is a plain row and the Risk Costs header is dropped. */
  get isRiskIncludedInPPM(): boolean {
    return this.commonDataService.getEstimateMetaData()?.estimateSettings?.includeRiskInPPM ?? false;
  }

  /** Rows of the main table; the project risk row joins them when risk is included in PPM. */
  get mainTableRows(): FormGroup<SolEstimateTableRowBaseExtended>[] {
    const rows = this.subMiscTravelFormArray.controls;
    if (!this.isRiskIncludedInPPM) {
      return rows;
    }
    const projectRiskRow = this.subRiskFormArray.controls.find(row => row.controls.name.value === this.projectRiskRowName);
    return projectRiskRow ? [...rows, projectRiskRow] : rows;
  }

  getRowLabelKey(rowName: string): string {
    return rowName === this.projectRiskRowName ? 'RISK_COSTS' : rowName;
  }

  get showMechanicalFee(): boolean {
    return this.miscRiskTravelCostsFormGroup.controls.showMechanicalFee?.value ?? true;
  }

  get showSubcontractorFee(): boolean {
    return this.solEstimateTableCommonFunctionsService.isSdiApplicable
      && (this.miscRiskTravelCostsFormGroup.controls.showSubcontractorFee.value ?? true);
  }

  isPricingModeLineItem(): boolean {
    return this.sellAsPricingMode === SolEstimatePricingMode.Line_Item;
  }

  openRiskDialog(): void {
    if (this.miscRiskTravelCostsFormGroup.disabled || !this.isAutoRiskApplicable) {
      return;
    }

    const data: SolEstimateRiskDialogData = {
      subtotal: this.riskDialogRequiredData.subtotal,
      techFee: this.riskDialogRequiredData.techFee,
      riskCost: this.calculateTotalTDC
    };
    this.solEstimateTableCommonFunctionsService.openRiskDialog(data);
  }

  private get isAutoRiskApplicable(): boolean {
    return !!this.commonDataService.getSQWebProject()
      && !this.commonDataService.isReadOnlyMode()
      && !this.commonDataService.globalPPMmode
      && (this.commonDataService.getEstimateMetaData()?.estimateSettings?.useAutoRisk ?? false);
  }

  private get calculateTotalTDC(): number {
    const calculatedRiskRow = this.subRiskFormArray.controls
      .find(row => row.controls.name.value === 'CALCULATED_RISK_COSTS');

    return calculatedRiskRow?.controls.cost.value ?? 0;
  }

  get isEstimateClosed(): boolean {
      return this.commonDataService.getEstimateId() === CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
